import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    GetActiveEducationUnitsApi,
    MarkAsCompletedUnitApi,
} from "../../../api/request/knowledgeHub.js";

const getImageUrl = (fileUrl) => {
    if (!fileUrl) return "";
    if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
        return fileUrl;
    }
    const base = process.env.REACT_APP_BASE_URL?.replace(/\/api\/?$/, "") || "";
    const filename = fileUrl.split(/[\\/]/).pop() || "";
    return filename ? `${base}/UploadFiles/EducationUnit/${filename}` : "";
};

const EducationSection = () => {
    const { sectionId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [units, setUnits] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isMarkingComplete, setIsMarkingComplete] = useState(false);

    const section = location.state?.section || {};
    const sectionTitle = section.title || "Section";
    const sectionNumber = section.sectionNumber ?? 1;
    const materialId = section.materialId;

    useEffect(() => {
        const fetchUnits = async () => {
            if (!sectionId) return;
            try {
                setIsLoading(true);
                const { data } = await GetActiveEducationUnitsApi(sectionId);
                if (data?.success) {
                    const sorted = (data?.data || []).sort(
                        (a, b) => (a.unitOrder ?? 0) - (b.unitOrder ?? 0)
                    );
                    setUnits(sorted);
                } else {
                    setUnits([]);
                }
            } catch (error) {
                console.error("Failed to fetch education units:", error);
                setUnits([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUnits();
    }, [sectionId]);

    const currentUnit = units[currentIndex];
    const totalUnits = units.length;
    const isLastUnit = currentIndex >= totalUnits - 1;

    const handleBack = () => {
        if (materialId) {
            navigate(`/education/${materialId}`, {
                state: { material: location.state?.material },
            });
        } else {
            navigate(-1);
        }
    };

    const handleNext = async () => {
        if (!currentUnit) return;

        try {
            setIsMarkingComplete(true);
            await MarkAsCompletedUnitApi({
                unitId: currentUnit.id,
                sectionId: sectionId,
            });

            if (isLastUnit) {
                handleBack();
            } else {
                setCurrentIndex((i) => i + 1);
            }
        } catch (error) {
            console.error("Failed to mark unit as complete:", error);
        } finally {
            setIsMarkingComplete(false);
        }
    };

    if (isLoading) {
        return (
            <div className="animate-pulse space-y-6 max-w-3xl mx-auto">
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="h-8 bg-gray-200 rounded w-2/3"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
            </div>
        );
    }

    if (!currentUnit) {
        return (
            <div className="max-w-3xl mx-auto text-center py-12">
                <p className="text-[#7A7A7A] mb-4">No units available.</p>
                <button
                    type="button"
                    onClick={handleBack}
                    className="px-4 py-2 rounded-lg border border-blue-500 bg-blue-50 text-blue-600 hover:bg-blue-100"
                >
                    Go Back
                </button>
            </div>
        );
    }

    const imageUrl = getImageUrl(currentUnit.fileUrl);

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            {/* Header */}
            <div>
                <p className="text-xs font-bold text-[#2F2B3D] uppercase tracking-wide">
                    Section {sectionNumber}
                </p>
                <h1 className="text-2xl font-bold text-[#2F2B3D] mt-1">
                    {sectionTitle}
                </h1>
                <div className="border-b border-gray-200 mt-4" />
            </div>

            {/* Unit Progress */}
            <p className="text-[#2F2B3D]">
                <span className="font-bold">Unit {currentIndex + 1}</span>
                <span className="text-[#7A7A7A] font-normal">
                    {" "}
                    / {totalUnits}
                </span>
            </p>

            {/* Image */}
            {imageUrl && (
                <div className="rounded-lg overflow-hidden bg-gray-100">
                    <img
                        src={imageUrl}
                        alt={currentUnit.title}
                        className="w-full h-auto object-contain"
                        onError={(e) => {
                            e.target.style.display = "none";
                        }}
                    />
                </div>
            )}

            {/* Description - HTML */}
            {currentUnit.description && (
                <div
                    className="text-[#2F2B3D] leading-relaxed [&>p]:mb-4 [&>p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{
                        __html: currentUnit.description,
                    }}
                />
            )}

            {/* Navigation Footer */}
            <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
                <button
                    type="button"
                    onClick={handleBack}
                    className="w-10 h-10 rounded-full flex items-center justify-center border-2 border-blue-500 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                    title="Back"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-5 h-5"
                    >
                        <path
                            fillRule="evenodd"
                            d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z"
                            clipRule="evenodd"
                        />
                    </svg>
                </button>
                <button
                    type="button"
                    onClick={handleNext}
                    disabled={isMarkingComplete}
                    className="px-6 py-2.5 rounded-full border-2 border-blue-500 bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isMarkingComplete
                        ? "Saving..."
                        : isLastUnit
                          ? "Finish"
                          : "Next Unit"}
                </button>
            </div>
        </div>
    );
};

export default EducationSection;
